import React, { useCallback } from 'react';
import { EmptyView, EMPTY_VIEW_ICONS } from '@shared/components/ui/EmptyView';
import { GroupingField, PendingOrderWithIds } from '../../hooks/usePendingOrders';
import { PendingOrderCard } from '../display/PendingOrderCard';

interface PendingOrdersGridProps {
    pendingOrders: PendingOrderWithIds[];
    totalRaw: number;
    groupingFields: Set<GroupingField>;
    selectedProducts: Set<string>;
    toggleProductSelection: (productKey: string) => void;
}

export const PendingOrdersGrid: React.FC<PendingOrdersGridProps> = ({
    pendingOrders,
    totalRaw,
    groupingFields,
    selectedProducts,
    toggleProductSelection
}) => {
    // 🎯 Crear callback para cada item específico - esto permite a React.memo optimizar mejor
    const createToggleCallback = useCallback((productKey: string) => {
        return () => toggleProductSelection(productKey);
    }, [toggleProductSelection]);

    if (pendingOrders.length === 0) {
        return (
            <EmptyView
                type={totalRaw === 0 ? 'emptyState' : 'noResults'}
                icon={EMPTY_VIEW_ICONS.ORDERS}
                iconSize={120}
                className="h-full min-h-0 py-10"
            />
        );
    }

    return (
        <div className="flex flex-col">
            <div className="">
                {pendingOrders.map((order, index) => {
                    // 🎯 Usar la misma clave que el hook (sin index para evitar duplicados)
                    const productKey = `${order.order_id}-${order.product.id}`;
                    return (
                        <PendingOrderCard
                            key={`${productKey}-${index}`} // Key único para React, pero productKey sin index para selección
                            order={order}
                            index={index}
                            groupingFields={groupingFields}
                            isSelected={selectedProducts.has(productKey)}
                            onToggleSelection={createToggleCallback(productKey)}
                        />
                    );
                })}
            </div>
        </div>
    );
};

// 🎯 Memorizar el componente principal también
export const PendingOrdersGridMemo = React.memo(PendingOrdersGrid, (prevProps, nextProps) => {
    // Comparar las props importantes
    return (
        prevProps.pendingOrders.length === nextProps.pendingOrders.length &&
        prevProps.totalRaw === nextProps.totalRaw &&
        prevProps.groupingFields === nextProps.groupingFields &&
        prevProps.selectedProducts === nextProps.selectedProducts &&
        prevProps.toggleProductSelection === nextProps.toggleProductSelection &&
        // Comparar los productos individuales (solo IDs para performance)
        prevProps.pendingOrders.every((order, index) =>
            order.product.id === nextProps.pendingOrders[index]?.product.id &&
            order.qty === nextProps.pendingOrders[index]?.qty
        )
    );
}); 